import React, { useEffect, useContext } from 'react';
import styled from 'styled-components';
import FooterV2 from 'components/footer_v2';
import CustomHelmet from 'components/CustomHelmet';
import EmptyState from 'components/EmptyState';
import CircularLoadingIndicator from 'components/indicators/CircularLoadingIndicator';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { UserSessionContext } from 'components/UserSession';
import { selectProductByIdAndLocale, fetchProductsById } from 'models/selectedProduct';
import { Product, LocalizedProduct } from 'models/types/products';
import findAvailableLocale from 'models/helpers/findAvailableLocale';
import { selectLocalizedEventName } from 'models/virtual_event';
import { TabsContextStateProvider } from 'components/TabsContext';
import FloatingFooter from 'components/footer_v2/FloatingFooter';
import ScrollableContainerWrapper from 'components/ScrollableContainerWrapper';
import Routes from 'Routes';
import ProductHighlight from './ProductHighlight';
import ProductDescriptionContainer from './ProductDescriptionContainer';

const RootContainer = styled.div`
  display: flex;
  margin: 0 auto;
  width: 100%;
  height: auto;
  padding: 16px;
  overflow-x: hidden;
  flex-direction: column;
  flex-shrink: 0;

  @media (max-width: 959px) {
    padding: 0;
  }
`;

const ProductDetailPage = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const productId = history.location.pathname.split('/')[2];
  const { userSession } = useContext(UserSessionContext);

  useEffect(() => {
    dispatch(fetchProductsById(productId)).then(({ payload: productObject }) => {
      const product = productObject as Product;
      const productName = findAvailableLocale(product.name, 'en');
      window.trackingItemToFirebase(userSession, 'Booth', 'Product Details Page', `View_Product_${product.id}`, productName, product.boothId, '');
    });
  }, [productId, dispatch]);

  const product: LocalizedProduct = useSelector((state) => (selectProductByIdAndLocale(state, productId))) ?? {} as LocalizedProduct;
  const fetchingProducts = useSelector((state) => (state as any).selectedProduct.fetching);
  const eventName = useSelector(selectLocalizedEventName);

  if (fetchingProducts) {
    return (
      <>
        <CircularLoadingIndicator />
        <FloatingFooter hashState={Routes.productDetail(productId)} />
      </>
    );
  }

  if (!Object.keys(product).length) {
    return (
      <EmptyState>
        <CustomHelmet>
          <meta charSet="utf-8" />
          <title>{`Product Not Found | ${eventName}`}</title>
          <meta name="description" content={`Product not found in ${eventName} event.`} />
        </CustomHelmet>
        Product not found
        <FloatingFooter hashState={Routes.productDetail(productId)} />
      </EmptyState>
    );
  }

  return (
    <TabsContextStateProvider>
      <ScrollableContainerWrapper WrapperContainer={RootContainer}>
        <ProductHighlight product={product} />
        <ProductDescriptionContainer product={product} />
        <FloatingFooter hashState={Routes.productDetail(productId)} />
      </ScrollableContainerWrapper>
    </TabsContextStateProvider>
  );
};

export default ProductDetailPage;
